import React from 'react';
import { useOnboarding, OnboardingIntent, OnboardingStep } from '../../contexts/OnboardingContext';
import { OnboardingProgress } from './components/OnboardingProgress';
import { IntentDiscoveryScreen } from './screens/IntentDiscoveryScreen';
import { Setup54DayScreen } from './screens/Setup54DayScreen';
import { SetupDailyRosaryScreen } from './screens/SetupDailyRosaryScreen';
import { NovenaExplorerScreen } from './screens/NovenaExplorerScreen';
import { FeaturePreviewScreen } from './screens/FeaturePreviewScreen';
import { MysteryType } from '../../types';

interface OnboardingFlowProps {
  onStart54DayNovena: (intention: string) => void;
  onStartRosary: (mystery: MysteryType) => void;
  onStartNovena: (novenaId: string) => void;
}

export const OnboardingFlow: React.FC<OnboardingFlowProps> = ({ onStart54DayNovena, onStartRosary, onStartNovena }) => {
  const {
    currentStep,
    setIntent,
    setStep,
    nextStep,
    previousStep,
    canGoBack,
    updateSetupData,
    completeOnboarding,
    getStepNumber,
    getTotalSteps,
  } = useOnboarding();

  const handleIntentSelected = (intent: OnboardingIntent) => {
    // Set the intent first, then manually determine and navigate to next step
    // We can't use nextStep() here because setState is async and selectedIntent won't be updated yet
    setIntent(intent);

    // Manually determine the next step based on the intent
    let nextStepName: OnboardingStep;
    switch (intent) {
      case '54-day-novena':
        nextStepName = 'novena-setup';
        break;
      case 'daily-rosary':
        nextStepName = 'rosary-setup';
        break;
      case 'explore-novenas':
        nextStepName = 'novena-explorer';
        break;
      case 'browsing':
        // User just wants to browse - complete onboarding and go to prayer selection screen
        completeOnboarding();
        return;
      default:
        return;
    }

    setStep(nextStepName);
  };

  const handle54DaySetup = (data: { intention: string; reminderTime: string; preReminder: boolean; enableReminders: boolean }) => {
    updateSetupData({
      novenaIntention: data.intention,
      reminderTime: data.reminderTime,
      enableReminders: data.enableReminders,
      reminderBefore: data.preReminder,
    });
    // Start the novena immediately with the intention
    onStart54DayNovena(data.intention);
    // Complete onboarding (novena is already started, just need to close onboarding UI)
    completeOnboarding();
  };

  const handleDailyRosarySetup = (data: {
    selectedMystery: MysteryType;
    reminderTime: string;
    enableReminders: boolean;
  }) => {
    updateSetupData({
      selectedMystery: data.selectedMystery,
      reminderTime: data.reminderTime,
      enableReminders: data.enableReminders,
    });
    // Complete onboarding and launch first prayer
    completeOnboarding(() => {
      onStartRosary(data.selectedMystery);
    });
  };

  const handleNovenaExplorer = (data: { selectedNovena?: string }) => {
    if (data.selectedNovena) {
      updateSetupData({
        selectedNovenaId: data.selectedNovena,
      });
      // Start the selected novena and complete onboarding
      completeOnboarding(() => {
        onStartNovena(data.selectedNovena!);
      });
    } else {
      // If no novena selected, just complete onboarding
      completeOnboarding();
    }
  };

  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 'feature-preview':
        return <FeaturePreviewScreen onContinue={nextStep} />;

      case 'intent-discovery':
        return <IntentDiscoveryScreen onContinue={handleIntentSelected} />;

      case 'novena-setup':
        return <Setup54DayScreen onContinue={handle54DaySetup} />;

      case 'rosary-setup':
        return <SetupDailyRosaryScreen onContinue={handleDailyRosarySetup} />;

      case 'novena-explorer':
        return <NovenaExplorerScreen onContinue={handleNovenaExplorer} />;

      default:
        return <div>Unknown step</div>;
    }
  };

  return (
    <div className="fixed inset-0 bg-white dark:bg-gray-900 z-50 flex flex-col transition-colors duration-300">
      {/* Header with progress */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 safe-area-top">
        <div className="max-w-2xl mx-auto relative py-4">
          {/* Back button */}
          {canGoBack() && (
            <button
              onClick={previousStep}
              className="absolute left-0 top-1/2 -translate-y-1/2 flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
              aria-label="Go back"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium">Back</span>
            </button>
          )}
          <OnboardingProgress currentStep={getStepNumber()} totalSteps={getTotalSteps()} />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-6 py-8 safe-area-bottom">
          {renderStep()}
        </div>
      </div>
    </div>
  );
};